// v3.0.0-rc.8 — Partner Console : upload CSV catalogues partenaires + preview + commit.
// Route : /ops-partners
// Auth  : x-admin-token (saisi au 1er load, stocké dans localStorage 'civ_ops_token').
// Flux  :
//   1. Sélectionner partenaire (Jumia / Sococé / Carrefour / Prosuma / Custom)
//   2. Déposer un CSV → preview (parse + valide) → tableau des 20 premières lignes + erreurs
//   3. Bouton "Importer" → commit (upsert par (partner, partner_sku))
//   4. Onglet Historique : liste des imports + bouton rollback
// Colonnes CSV attendues :
//   Obligatoires : sku, name, price_fcfa, category
//   Optionnelles : image_url, stock, brand, description, tags (séparateur | ou ;)

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api/v3/ops/partners`;
const TOKEN_KEY = "civ_ops_token";
const KNOWN_PARTNERS = [
  { id: "jumia", label: "Jumia", color: "#F68B1E" },
  { id: "sococe", label: "Sococé", color: "#0057A0" },
  { id: "carrefour", label: "Carrefour CI", color: "#004E9F" },
  { id: "prosuma", label: "Prosuma", color: "#E31E24" },
  { id: "custom", label: "Custom", color: "#6366F1" },
];

const REQUIRED_COLS = ["sku", "name", "price_fcfa", "category"];
const OPTIONAL_COLS = ["image_url", "stock", "brand", "description", "tags"];

function fmtFcfa(n) {
  if (!Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString("fr-FR") + " FCFA";
}

function useAdminToken() {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY) || ""; } catch (_) { return ""; }
  });
  useEffect(() => { try { localStorage.setItem(TOKEN_KEY, token); } catch (_) { /* noop */ } }, [token]);
  return [token, setToken];
}

async function apiCall(path, options = {}, token) {
  const headers = { ...(options.headers || {}) };
  headers["x-admin-token"] = token;
  const r = await fetch(`${API}${path}`, { ...options, headers });
  const isJson = (r.headers.get("content-type") || "").includes("application/json");
  const body = isJson ? await r.json() : { raw: await r.text() };
  return { ok: r.ok, status: r.status, body };
}

export default function PartnerConsolePage() {
  const [token, setToken] = useAdminToken();
  const [tab, setTab] = useState("upload"); // upload | history | products

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(180deg, #F8FAFC 0%, #EEF2F7 100%)",
      color: "#0F172A",
      fontFamily: "system-ui, -apple-system, sans-serif",
    }}>
      <header style={{
        padding: "22px 28px",
        borderBottom: "1px solid rgba(15,23,42,0.08)",
        background: "#fff",
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: "linear-gradient(135deg, #FF7E1B, #F59E0B)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 24, color: "#fff",
        }}>📦</div>
        <div style={{ flex: 1 }}>
          <h1 style={{
            margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: -0.5,
          }}>Console Partenaires — AYA</h1>
          <div style={{ fontSize: 12.5, opacity: 0.65, marginTop: 2 }}>
            Import CSV des catalogues Jumia · Sococé · Carrefour · Prosuma · Custom
          </div>
        </div>
        <TokenBadge token={token} setToken={setToken} />
      </header>

      <nav style={{
        display: "flex", gap: 4, padding: "12px 28px 0",
        background: "#fff",
        borderBottom: "1px solid rgba(15,23,42,0.06)",
      }}>
        {[
          { k: "upload", label: "📤 Import CSV" },
          { k: "history", label: "🕰️ Historique" },
          { k: "products", label: "🛍️ Produits ingérés" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`partner-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            style={{
              padding: "10px 16px",
              background: "transparent",
              color: tab === t.k ? "#FF7E1B" : "#334155",
              border: "none",
              borderBottom: tab === t.k ? "3px solid #FF7E1B" : "3px solid transparent",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 13.5,
              marginBottom: -1,
            }}
          >{t.label}</button>
        ))}
      </nav>

      <main style={{ padding: "24px 28px", maxWidth: 1200, margin: "0 auto" }}>
        {!token && (
          <div style={{
            padding: 16, background: "#FEF3C7", border: "1px solid #F59E0B",
            borderRadius: 10, color: "#92400E", fontSize: 13.5,
          }}>
            🔒 Saisis le token admin dans le badge en haut à droite pour accéder aux endpoints.
          </div>
        )}
        {token && tab === "upload" && <UploadTab token={token} />}
        {token && tab === "history" && <HistoryTab token={token} />}
        {token && tab === "products" && <ProductsTab token={token} />}
      </main>
    </div>
  );
}

function TokenBadge({ token, setToken }) {
  const [editing, setEditing] = useState(!token);
  const [draft, setDraft] = useState(token);
  useEffect(() => setDraft(token), [token]);
  if (editing) {
    return (
      <div style={{ display: "flex", gap: 6 }}>
        <input
          data-testid="partner-token-input"
          type="password"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="x-admin-token"
          style={{
            padding: "7px 10px", borderRadius: 8,
            border: "1px solid #cbd5e1", fontSize: 13,
            width: 220,
          }}
        />
        <button
          data-testid="partner-token-save"
          onClick={() => { setToken(draft); setEditing(false); }}
          style={{
            padding: "7px 14px", borderRadius: 8,
            background: "#0F172A", color: "#fff", border: "none",
            fontWeight: 700, fontSize: 13, cursor: "pointer",
          }}
        >OK</button>
      </div>
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      title="Modifier le token admin"
      style={{
        padding: "7px 12px", borderRadius: 8,
        background: "#ECFDF5", color: "#065F46",
        border: "1px solid #10B981", fontSize: 12, cursor: "pointer",
        fontWeight: 700,
      }}
    >🔑 Token OK</button>
  );
}

function UploadTab({ token }) {
  const [partner, setPartner] = useState("jumia");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const runPreview = useCallback(async (f) => {
    if (!f) return;
    setLoading(true); setError(null); setPreview(null); setCommitResult(null);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("partner", partner);
    const { ok, body } = await apiCall("/import/preview", { method: "POST", body: fd }, token);
    setLoading(false);
    if (!ok) { setError(body.error || "preview_failed"); return; }
    setPreview(body);
  }, [partner, token]);

  const runCommit = useCallback(async () => {
    if (!file) return;
    setLoading(true); setError(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("partner", partner);
    const { ok, body } = await apiCall("/import/commit", { method: "POST", body: fd }, token);
    setLoading(false);
    if (!ok) { setError(body.error || "commit_failed"); return; }
    setCommitResult(body);
  }, [file, partner, token]);

  const onFileChange = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    runPreview(f);
  }, [runPreview]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Partner selector */}
      <section style={cardStyle}>
        <h3 style={sectionTitle}>1. Choisir le partenaire</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {KNOWN_PARTNERS.map((p) => (
            <button
              key={p.id}
              data-testid={`partner-btn-${p.id}`}
              onClick={() => setPartner(p.id)}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: partner === p.id ? `2px solid ${p.color}` : "1px solid #cbd5e1",
                background: partner === p.id ? `${p.color}18` : "#fff",
                color: partner === p.id ? p.color : "#334155",
                fontWeight: 700, fontSize: 13,
                cursor: "pointer",
              }}
            >{p.label}</button>
          ))}
        </div>
      </section>

      {/* Drop zone */}
      <section style={cardStyle}>
        <h3 style={sectionTitle}>2. Déposer un fichier CSV</h3>
        <p style={{ fontSize: 12.5, opacity: 0.7, margin: "0 0 12px" }}>
          Colonnes obligatoires : <code style={codeChip}>sku</code>{" "}
          <code style={codeChip}>name</code>{" "}
          <code style={codeChip}>price_fcfa</code>{" "}
          <code style={codeChip}>category</code><br />
          Optionnelles : <code style={codeChip}>image_url</code>{" "}
          <code style={codeChip}>stock</code>{" "}
          <code style={codeChip}>brand</code>{" "}
          <code style={codeChip}>description</code>{" "}
          <code style={codeChip}>tags</code> (séparateur | ou ;)
        </p>
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f) { setFile(f); runPreview(f); }
          }}
          style={{
            border: "2px dashed #cbd5e1",
            borderRadius: 12,
            padding: "32px 20px",
            textAlign: "center",
            cursor: "pointer",
            background: "#F8FAFC",
            transition: "background 150ms, border-color 150ms",
          }}
        >
          <div style={{ fontSize: 36, marginBottom: 8 }}>📤</div>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
            {file ? file.name : "Clique ou glisse un CSV ici"}
          </div>
          <div style={{ fontSize: 12, opacity: 0.6 }}>
            {file
              ? `${Math.round(file.size / 1024)} KB · Prêt à prévisualiser`
              : "Max 8 MB, encodé UTF-8"}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            data-testid="partner-file-input"
            onChange={onFileChange}
            style={{ display: "none" }}
          />
        </div>
      </section>

      {loading && <div style={{ padding: 12, opacity: 0.7 }}>⏳ Traitement…</div>}
      {error && (
        <div data-testid="partner-error" style={{
          padding: 14, background: "#FEE2E2", border: "1px solid #EF4444",
          borderRadius: 10, color: "#991B1B", fontSize: 13,
        }}>❌ {error}</div>
      )}

      {preview && (
        <section style={cardStyle} data-testid="partner-preview">
          <h3 style={sectionTitle}>3. Aperçu</h3>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 10, marginBottom: 16,
          }}>
            <Stat label="Total lignes" value={preview.total_rows} />
            <Stat label="Valides" value={preview.valid_count} color="#10B981" />
            <Stat label="En erreur" value={preview.error_count} color={preview.error_count > 0 ? "#EF4444" : "#94A3B8"} />
            <Stat label="Fichier" value={preview.filename} small />
          </div>

          {preview.errors && preview.errors.length > 0 && (
            <details style={{
              background: "#FEF3C7", borderRadius: 8, padding: 10, marginBottom: 12,
              fontSize: 12,
            }}>
              <summary style={{ cursor: "pointer", fontWeight: 700, color: "#92400E" }}>
                ⚠️ {preview.errors.length} lignes en erreur (cliquer pour voir)
              </summary>
              <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                {preview.errors.map((e, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    Ligne {e.line} — sku=<code>{e.sku || "∅"}</code> — {e.issues.join(", ")}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {preview.sample_products && preview.sample_products.length > 0 && (
            <PreviewTable rows={preview.sample_products} />
          )}

          <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              onClick={() => { setPreview(null); setFile(null); setCommitResult(null); }}
              style={ghostBtn}
              data-testid="partner-cancel"
            >Annuler</button>
            <button
              onClick={runCommit}
              disabled={loading || preview.valid_count === 0}
              data-testid="partner-commit"
              style={{
                ...primaryBtn,
                opacity: (loading || preview.valid_count === 0) ? 0.5 : 1,
              }}
            >
              ✅ Importer {preview.valid_count} produit{preview.valid_count > 1 ? "s" : ""}
            </button>
          </div>
        </section>
      )}

      {commitResult && (
        <section data-testid="partner-commit-result" style={{
          ...cardStyle, borderLeft: "4px solid #10B981",
        }}>
          <h3 style={{ ...sectionTitle, color: "#10B981" }}>✅ Import réussi</h3>
          <div style={{ fontSize: 13.5, lineHeight: 1.7 }}>
            <div><strong>Batch:</strong> <code>{commitResult.batch_id}</code></div>
            <div><strong>Partenaire:</strong> {commitResult.partner}</div>
            <div><strong>Insérés:</strong> {commitResult.inserted}</div>
            <div><strong>Mis à jour:</strong> {commitResult.updated}</div>
            <div><strong>Erreurs skippées:</strong> {commitResult.error_count}</div>
          </div>
        </section>
      )}
    </div>
  );
}

function HistoryTab({ token }) {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { ok, body } = await apiCall("/imports?limit=100", { method: "GET" }, token);
    setLoading(false);
    if (ok) setImports(body.imports || []);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const rollback = useCallback(async (batch_id) => {
    if (!window.confirm(`Supprimer le batch ${batch_id} et ses produits ?`)) return;
    const { ok } = await apiCall(`/imports/${batch_id}`, { method: "DELETE" }, token);
    if (ok) load();
  }, [token, load]);

  if (loading) return <div style={{ padding: 20, opacity: 0.65 }}>⏳ Chargement…</div>;
  if (imports.length === 0) {
    return (
      <div style={{ ...cardStyle, textAlign: "center", padding: 32, opacity: 0.7 }}>
        Aucun import pour le moment. Va sur l&apos;onglet <strong>Import CSV</strong> pour commencer.
      </div>
    );
  }
  return (
    <div style={cardStyle} data-testid="partner-history">
      <h3 style={sectionTitle}>Historique des imports</h3>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Date</th>
            <th style={thStyle}>Partenaire</th>
            <th style={thStyle}>Fichier</th>
            <th style={thStyle}>Insérés / MAJ</th>
            <th style={thStyle}>Erreurs</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {imports.map((imp) => (
            <tr key={imp.batch_id} data-testid={`partner-history-row-${imp.batch_id}`}>
              <td style={tdStyle}>{new Date(imp.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</td>
              <td style={tdStyle}>
                <span style={{ ...pillStyle, background: "#EEF2F7", color: "#334155" }}>{imp.partner}</span>
              </td>
              <td style={tdStyle}>{imp.filename}</td>
              <td style={tdStyle}>
                <strong style={{ color: "#10B981" }}>+{imp.inserted}</strong>{" "}
                / <span style={{ color: "#6366F1" }}>~{imp.updated}</span>
              </td>
              <td style={tdStyle}>
                {imp.error_count > 0
                  ? <span style={{ color: "#EF4444", fontWeight: 700 }}>{imp.error_count}</span>
                  : <span style={{ opacity: 0.4 }}>0</span>}
              </td>
              <td style={tdStyle}>
                <button
                  onClick={() => rollback(imp.batch_id)}
                  style={{
                    padding: "5px 10px", borderRadius: 6, background: "#FEE2E2",
                    color: "#991B1B", border: "1px solid #EF4444",
                    fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                  }}
                >🗑️ Rollback</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProductsTab({ token }) {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ partner: "", q: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter.partner) params.append("partner", filter.partner);
    if (filter.q) params.append("q", filter.q);
    params.append("limit", "50");
    const { ok, body } = await apiCall(`/products?${params}`, { method: "GET" }, token);
    setLoading(false);
    if (ok) { setProducts(body.products || []); setTotal(body.total || 0); }
  }, [token, filter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div style={cardStyle} data-testid="partner-products">
      <h3 style={sectionTitle}>Produits ingérés — <span style={{ opacity: 0.6, fontWeight: 500 }}>{total} au total</span></h3>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <select
          value={filter.partner}
          onChange={(e) => setFilter((f) => ({ ...f, partner: e.target.value }))}
          data-testid="partner-products-filter"
          style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}
        >
          <option value="">Tous les partenaires</option>
          {KNOWN_PARTNERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <input
          type="text"
          placeholder="Rechercher un produit…"
          value={filter.q}
          data-testid="partner-products-search"
          onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
          style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}
        />
      </div>
      {loading && <div style={{ opacity: 0.6, padding: 12 }}>⏳</div>}
      {!loading && products.length === 0 && (
        <div style={{ padding: 20, opacity: 0.65, textAlign: "center" }}>Aucun produit correspondant.</div>
      )}
      {!loading && products.length > 0 && (
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 10,
        }}>
          {products.map((p) => (
            <article key={p.product_id} style={{
              border: "1px solid #E2E8F0", borderRadius: 10, padding: 10,
              background: "#fff",
            }}>
              {p.image_url && (
                <img src={p.image_url} alt=""
                  style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 6, marginBottom: 8 }}
                  onError={(e) => { e.target.style.display = "none"; }} />
              )}
              <div style={{ fontSize: 11, opacity: 0.55, marginBottom: 2 }}>
                {p.partner} · {p.category}
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 4, lineHeight: 1.3 }}>{p.name}</div>
              <div style={{ fontSize: 12.5, color: "#059669", fontWeight: 700 }}>{fmtFcfa(p.price_fcfa)}</div>
              <div style={{ fontSize: 10.5, opacity: 0.5, marginTop: 4 }}>SKU {p.partner_sku}</div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function PreviewTable({ rows }) {
  const cols = useMemo(() => [...REQUIRED_COLS, ...OPTIONAL_COLS], []);
  return (
    <div style={{ overflowX: "auto", border: "1px solid #E2E8F0", borderRadius: 10 }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>#</th>
            {cols.map((c) => <th key={c} style={thStyle}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ ...tdStyle, opacity: 0.55 }}>{r._line}</td>
              {cols.map((c) => (
                <td key={c} style={{ ...tdStyle, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c === "price_fcfa" && r[c] != null ? fmtFcfa(r[c]) :
                    c === "tags" && Array.isArray(r[c]) ? r[c].join(", ") :
                    (r[c] == null ? "" : String(r[c]))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value, color, small }) {
  return (
    <div style={{
      padding: 12, background: "#F8FAFC", borderRadius: 8, textAlign: "center",
      border: "1px solid #E2E8F0",
    }}>
      <div style={{
        fontSize: small ? 12 : 22, fontWeight: 800,
        color: color || "#0F172A", lineHeight: 1.2,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{value ?? "—"}</div>
      <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
    </div>
  );
}

// ─── styles ────────────────────────────────────────────────────────────────
const cardStyle = {
  background: "#fff",
  border: "1px solid rgba(15,23,42,0.06)",
  borderRadius: 14,
  padding: 18,
  boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
};
const sectionTitle = {
  margin: "0 0 12px",
  fontSize: 14.5,
  fontWeight: 700,
  color: "#0F172A",
};
const codeChip = {
  padding: "1px 6px",
  borderRadius: 4,
  background: "rgba(99,102,241,0.10)",
  fontSize: 11,
  fontFamily: "ui-monospace, Menlo, monospace",
};
const primaryBtn = {
  padding: "10px 18px", borderRadius: 10,
  background: "#0F172A", color: "#fff", border: "none",
  fontWeight: 700, fontSize: 13.5, cursor: "pointer",
};
const ghostBtn = {
  padding: "10px 18px", borderRadius: 10,
  background: "#fff", color: "#334155",
  border: "1px solid #cbd5e1",
  fontWeight: 600, fontSize: 13.5, cursor: "pointer",
};
const tableStyle = {
  width: "100%", borderCollapse: "collapse", fontSize: 12.5,
};
const thStyle = {
  textAlign: "left",
  padding: "8px 10px",
  fontSize: 11, fontWeight: 700, textTransform: "uppercase",
  letterSpacing: 0.4, color: "#64748B",
  background: "#F8FAFC",
  borderBottom: "1px solid #E2E8F0",
};
const tdStyle = {
  padding: "8px 10px",
  borderBottom: "1px solid #F1F5F9",
};
const pillStyle = {
  padding: "3px 8px", borderRadius: 999,
  fontSize: 11, fontWeight: 700, textTransform: "uppercase",
  letterSpacing: 0.4,
};
